// Per spec #29 / #82 / #99: every authenticated request carries the
// `Authorization: Bearer <access_token>` header; writes should send an
// `Idempotency-Key` and a fresh `X-Request-ID`. The mobile client
// already does this through Dio interceptors — the web client does
// the same via `fetchWithAuth` so the surface stays symmetric.
//
// Phase 1.5: a reactive 401 → refresh → retry path is layered on top.
// `fetchWithAuth` accepts an optional `onUnauthorized` callback; if the
// callback resolves with a fresh access token, the original request is
// retried exactly once (carrying `X-Web-Retry: 1` so the server and the
// interceptor can both recognise the retry and refuse infinite loops).
// The callback is expected to dedupe concurrent refresh attempts —
// `AuthContext.refreshAccessToken()` owns that responsibility so this
// module stays transport-only.
import { ApiException } from './errors';
import { appConfig } from '@/config/appConfig';

export type SessionTokenProvider = () => string | null;

/**
 * Called by `fetchWithAuth` when an authenticated request returns 401.
 * Must return the new access token (after refresh) or `null` if the
 * session is unrecoverable. Implementations MUST deduplicate concurrent
 * refresh attempts so that N parallel 401s trigger exactly one
 * `/auth/refresh` round-trip.
 */
export type UnauthorizedHandler = () => Promise<string | null>;

let defaultUnauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Installs the default reactive 401 handler used by every call site
 * that doesn't pass its own `onUnauthorized`. `AuthContext` calls this
 * once at mount with a dedup'd `refreshAccessToken` so any HTTP request
 * from anywhere in the tree benefits from the 401 → refresh → retry
 * behaviour.
 */
export function setDefaultUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  defaultUnauthorizedHandler = handler;
}

export interface FetchOptions extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  headers?: Record<string, string>;
  /** Skip the Authorization header (e.g. `/auth/login`). */
  anonymous?: boolean;
  /** Request timeout in ms (default: 30000). */
  timeout?: number;
  /**
   * Optional reactive 401 handler. If the request returns 401, this
   * hook is invoked once; if it resolves with a fresh token, the
   * original request is retried once with `X-Web-Retry: 1`.
   * Falls back to the module-level handler set by
   * `setDefaultUnauthorizedHandler` (installed by `AuthContext`).
   */
  onUnauthorized?: UnauthorizedHandler;
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export async function fetchWithAuth(
  path: string,
  getToken: SessionTokenProvider,
  opts: FetchOptions = {},
): Promise<Response> {
  // Phase 1.5: the 401 path retries through `onUnauthorized` at most once.
  // `retryAttempted` is a local closure flag so a misbehaving callback
  // (e.g. resolves with the same expired token) cannot trigger an
  // infinite retry loop.
  let retryAttempted = false;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await performFetch(path, getToken, opts, retryAttempted);

    // Anonymous calls (e.g. /auth/login) can return 401 without it being
    // a session-expiry signal; let the caller see the original error.
    if (res.status !== 401 || opts.anonymous === true || retryAttempted) {
      if (!res.ok) {
        throw await ApiException.fromResponse(res);
      }
      return res;
    }

    const handler = opts.onUnauthorized ?? defaultUnauthorizedHandler;
    if (!handler) {
      throw await ApiException.fromResponse(res);
    }

    const freshToken = await handler();
    if (!freshToken) {
      // Refresh failed (or the user signed out). Surface the original 401
      // so the caller / AuthContext can react.
      throw await ApiException.fromResponse(res);
    }
    retryAttempted = true;
    // Loop again — the next iteration reads the new token via
    // `getToken()` (the callback injected by AuthContext closes over the
    // current React state) and sets `X-Web-Retry: 1`.
  }
}

async function performFetch(
  path: string,
  getToken: SessionTokenProvider,
  opts: FetchOptions,
  isRetry: boolean,
): Promise<Response> {
  const url = path.startsWith('http')
    ? path
    : `${appConfig.apiBaseUrl}/api/${appConfig.apiVersion}${path}`;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Client-Version': '0.1.0',
    ...(opts.headers ?? {}),
  };

  if (opts.body !== undefined && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
  }

  if (!opts.anonymous) {
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  // Idempotency-Key is regenerated on every attempt: the server treats a
  // replayed key as a duplicate, which would silently swallow the retry
  // attempt. A fresh key per attempt keeps the retry semantically distinct
  // from the original.
  if (opts.method && opts.method !== 'GET' && opts.method !== 'HEAD') {
    headers['Idempotency-Key'] = uuid();
  }
  headers['X-Request-ID'] = headers['X-Request-ID'] ?? uuid();
  if (isRetry) headers['X-Web-Retry'] = '1';

  const init: RequestInit = {
    ...opts,
    headers,
    body:
      opts.body === undefined
        ? undefined
        : opts.body instanceof FormData
          ? opts.body
          : typeof opts.body === 'string'
            ? opts.body
            : JSON.stringify(opts.body),
  };

  const timeout = opts.timeout ?? 30_000;

  let res: Response;
  try {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeout);
    try {
      res = await fetch(url, { ...init, signal: controller.signal });
    } finally {
      window.clearTimeout(timeoutId);
    }
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') {
      throw new ApiException({
        statusCode: 0,
        code: 'REQUEST_TIMEOUT',
        message: `Request timed out after ${timeout}ms`,
      });
    }
    throw new ApiException({
      statusCode: 0,
      code: 'NETWORK_ERROR',
      message: e instanceof Error ? e.message : 'Network error',
    });
  }

  return res;
}

export async function fetchJson<T>(
  path: string,
  getToken: SessionTokenProvider,
  opts: FetchOptions = {},
): Promise<T> {
  const res = await fetchWithAuth(path, getToken, opts);
  if (res.status === 204) return undefined as T;
  const body = (await res.json()) as { data?: T } | T;
  // Some endpoints wrap the payload in `{ data: … }`; others (notably
  // photo / report lists from Phase 1-9) return a bare payload. Both are
  // accepted — when the envelope wraps an object/array, unwrap it.
  if (body !== null && typeof body === 'object' && 'data' in body) {
    return (body as { data?: T }).data as T;
  }
  return body as T;
}

export interface ListEnvelope<T> {
  items: T[];
  total?: number;
  next_cursor?: string | null;
}

export interface ListResponse<T> {
  items: T[];
  total?: number;
  nextCursor?: string | null;
}

/**
 * Fetch a list endpoint that returns `{ data: { items, total, next_cursor? } }`.
 *
 * Some Phase 10 endpoints (friends / shares / friend search) follow this
 * envelope — they include `total` for badge counts and an optional
 * `next_cursor` for pagination. Calling code gets back a plain array
 * (plus the optional total for badge UIs).
 *
 * Bare-array endpoints (Phase 1-9 reports / photos) keep using `fetchJson`.
 */
export async function fetchJsonList<T>(
  path: string,
  getToken: SessionTokenProvider,
  opts: FetchOptions = {},
): Promise<ListResponse<T>> {
  const env = await fetchJson<ListEnvelope<T>>(path, getToken, opts);
  // Defense in depth: an endpoint that should have been wrapped may
  // occasionally degrade to a bare array (older Stage 0 routes). Detect
  // and coerce so the caller's `.map()` never crashes on a non-array.
  let items: T[];
  let total: number | undefined;
  let nextCursor: string | null | undefined;
  if (Array.isArray(env)) {
    items = env;
  } else if (env && typeof env === 'object' && Array.isArray((env as ListEnvelope<T>).items)) {
    const e = env as ListEnvelope<T>;
    items = e.items;
    total = typeof e.total === 'number' ? e.total : undefined;
    nextCursor = e.next_cursor ?? null;
  } else if (
    env &&
    typeof env === 'object' &&
    'items' in (env as unknown as Record<string, unknown>)
  ) {
    // Server returned `{data:{items:null}}` or some similar envelope —
    // fall back to an empty list rather than crashing the caller.
    items = [];
  } else {
    items = [];
  }
  return { items, total, nextCursor };
}
